import { useEffect, useRef } from 'react'

type UseBarcodeScannerOptions = {
  enabled?: boolean
  minLength?: number
  resetDelayMs?: number
  onScan: (code: string) => void
}

export function useBarcodeScanner({
  enabled = true,
  minLength = 3,
  resetDelayMs = 120,
  onScan,
}: UseBarcodeScannerOptions) {
  const bufferRef = useRef('')
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) return

    const resetBuffer = () => {
      bufferRef.current = ''
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const scheduleReset = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        bufferRef.current = ''
        timerRef.current = null
      }, resetDelayMs)
    }

    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()

      const isTypingField =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.getAttribute('contenteditable') === 'true'

      if (isTypingField) return

      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'Meta'
      ) {
        return
      }

      if (e.key === 'Enter') {
        const scanned = bufferRef.current.trim()
        if (scanned.length >= minLength) {
          onScan(scanned)
        }
        resetBuffer()
        return
      }

      if (e.key.length !== 1) return

      bufferRef.current += e.key
      scheduleReset()
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
      }
    }
  }, [enabled, minLength, onScan, resetDelayMs])
}
